"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { TaskRunEvent } from "@mlx/contracts";
import { useStreamingMarkdownRehypePlugins } from "@product/components/app/runs/assistant-response-streaming";
import { RunLiveUpdateLine } from "@product/components/app/runs/run-live-update-line";
import { formatUpdateCount, runActivityLabel } from "@product/components/app/runs/run-trace-labels";
import { AssistantMessageColumn, TranscriptTurnView } from "@product/components/app/runs/transcript-turn-view";
import { markdownRehypePlugins } from "@product/components/content/markdown-math";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { formatRunDuration } from "@product/lib/run-details-format";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { ShotComposer, ShotRail } from "./chrome";
import { ShotMarkdown } from "./markdown";
import { ReplayControls } from "./replay-controls";
import {
  phoneTimeline,
  replayAnswerAt,
  replayBlocksAt,
  replayCameraAt,
  replayEvents,
  replayLiveUpdateAt,
  replayRunClockAt,
  type ReplayCamera,
  type ReplayTimeline,
} from "./replay-run";
import type { ReplayStory } from "./replay-story";
import { ShotRecordSheet } from "./record-sheet";
import { ShotProvenance, ShotUserTurn } from "./turn";
import { useReplayClock } from "./use-replay-clock";
import { ShotAnswerFooter, ShotClosedTrace, ShotWindow } from "./window";

/** The replay is watched, not operated: its frame is inert. */
const noop = () => {};

/** Where the phone layout starts: the app's `sm` breakpoint. */
const PHONE_QUERY = "(max-width: 639px)";

/** Scrolls the transcript so `target` sits just below its top edge. */
function scrollIntoTranscript(transcript: HTMLElement, target: Element, behavior: ScrollBehavior) {
  const top = transcript.scrollTop + target.getBoundingClientRect().top - transcript.getBoundingClientRect().top - 16;
  transcript.scrollTo({ top, behavior });
}

/**
 * Points the transcript where the camera looks: the live edge, the answer's
 * opening, its table, or the table swiped to its last column (the answer's
 * own sideways-scrolling strip, as a reader's swipe moves it).
 */
function pointCamera(transcript: HTMLElement, camera: ReplayCamera, behavior: ScrollBehavior) {
  const strip = transcript.querySelector<HTMLElement>(".typeset-scroll");
  if (camera !== "swipe") strip?.scrollTo({ left: 0, behavior });
  if (camera === "follow") {
    transcript.scrollTo({ top: transcript.scrollHeight, behavior });
    return;
  }
  const answer = transcript.querySelector(".typeset");
  if (camera === "answer" && answer) scrollIntoTranscript(transcript, answer, behavior);
  if ((camera === "table" || camera === "swipe") && strip) {
    scrollIntoTranscript(transcript, strip, behavior);
    if (camera === "swipe") strip.scrollTo({ left: strip.scrollWidth, behavior });
  }
}

/** The answer as it streams, through the product's own streaming reveal. */
function StreamingAnswer({ text, streaming, runId }: { text: string; streaming: boolean; runId: string }) {
  const normalized = normalizeMarkdownContent(text, { streaming });
  const streamingPlugins = useStreamingMarkdownRehypePlugins({
    enabled: streaming,
    source: normalized,
    streamIdentity: runId,
  });
  return (
    <ShotMarkdown
      streaming={streaming}
      rehypePlugins={streamingPlugins ? [...streamingPlugins, ...markdownRehypePlugins] : undefined}
    >
      {text}
    </ShotMarkdown>
  );
}

/**
 * A replay's frame at `atMs`: the question sent, the run's progress line and
 * trace pill, the answer streaming in, its provenance once complete, then
 * one of the records the answer links opening. Every state is the product's
 * own component in the state the run's events put it in.
 */
export function ReplayFrame({
  replay,
  timeline,
  events,
  tone,
  atMs,
  reduceMotion,
}: {
  replay: ReplayStory;
  /** The story's timeline, or its phone timeline. */
  timeline: ReplayTimeline;
  events: readonly TaskRunEvent[];
  tone: AppThemeTone;
  atMs: number;
  reduceMotion: boolean;
}) {
  const { story, run } = replay;
  const { workspace } = story;
  const runMs = replayRunClockAt(timeline, run, atMs);
  const completed = runMs >= run.durationMs;
  const update = replayLiveUpdateAt(run, runMs);
  const answer = replayAnswerAt(run, events, runMs);
  const blocks = replayBlocksAt(run, events, runMs);
  const transcriptRef = useRef<HTMLElement>(null);
  const recordIndex = story.records.findIndex((record) => record.id === replay.openRecordId);
  const record = story.records[recordIndex];

  const camera = replayCameraAt(timeline, atMs);

  // The transcript follows the live edge while the answer grows, then goes
  // where the phone timeline's cues point it.
  useLayoutEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    pointCamera(transcript, camera, reduceMotion ? "auto" : "smooth");
  }, [answer.length, completed, camera, reduceMotion]);

  // Where the open record outgrows the frame (a phone), its details scroll
  // to the last field once the first are read, as a reader scrolls them.
  const sheetAtEnd = timeline.sheetScrollMs !== undefined && atMs >= timeline.sheetScrollMs;
  useLayoutEffect(() => {
    const surface = transcriptRef.current?.closest("[data-app-surface]");
    const details = surface?.querySelector<HTMLElement>('[role="dialog"] .overflow-auto');
    if (!details) return;
    details.scrollTo({ top: sheetAtEnd ? details.scrollHeight : 0, behavior: reduceMotion ? "auto" : "smooth" });
  }, [sheetAtEnd, reduceMotion]);

  return (
    <ShotWindow
      tone={tone}
      workspace={workspace}
      chromeInert
      transcriptRef={transcriptRef}
      rail={<ShotRail workspace={workspace} sessions={completed ? workspace.sessions : replay.runningSessions} />}
      transcript={
        <TranscriptTurnView user={<ShotUserTurn question={story.question} user={workspace.user} times={story.times} />}>
          {/* As the transcript item places them: the progress line above
              the answer column, which appears once there is an answer. */}
          {update ? (
            <RunLiveUpdateLine
              items={[{ key: update.id, text: update.text, type: "progress" }]}
              reduceMotion={reduceMotion}
            />
          ) : null}
          {answer ? (
            <AssistantMessageColumn>
              <StreamingAnswer text={answer} streaming={!completed} runId={run.id} />
              {completed ? (
                <ShotProvenance
                  workingSeconds={10}
                  user={workspace.user}
                  sources={story.provenance?.sources}
                  plugins={story.provenance?.plugins}
                />
              ) : null}
            </AssistantMessageColumn>
          ) : null}
        </TranscriptTurnView>
      }
      footer={
        <ShotAnswerFooter
          showHelpImprove={completed}
          trace={
            <ShotClosedTrace
              label={runActivityLabel(completed)}
              updateCountLabel={formatUpdateCount(blocks.length)}
              durationLabel={
                completed
                  ? formatRunDuration(run.durationMs)
                  : runMs < 1_000
                    ? null
                    : formatRunDuration(Math.floor(runMs / 1_000) * 1_000)
              }
              running={!completed}
            />
          }
        />
      }
      composer={<ShotComposer busy={!completed} sources={workspace.composerSources} />}
      overlay={
        atMs >= timeline.recordOpensMs && record ? (
          <ShotRecordSheet
            record={record}
            onClose={noop}
            focusOnOpen={false}
            navigation={{ index: recordIndex, total: story.records.length }}
          />
        ) : null
      }
      aria-hidden="true"
      inert
    />
  );
}

/**
 * The replay's player: runs the timeline, pauses it (and the product's own
 * CSS motion) on request or while the shot is off screen, and replays it
 * from the start. With reduced motion it opens on the final frame and plays
 * only when asked.
 */
export function ReplayPlayer({
  replay,
  tone,
  reduceMotion,
}: {
  replay: ReplayStory;
  tone: AppThemeTone;
  reduceMotion: boolean;
}) {
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const change = () => setPhone(query.matches);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);
  const timeline = useMemo(() => (phone ? phoneTimeline(replay.timeline) : replay.timeline), [phone, replay]);
  const endsMs = timeline.endsMs;
  const events = useMemo(() => replayEvents(replay.run, replay.story.answerMarkdown), [replay]);
  const { ref: playerRef, atMs, playing, ended, toggle } = useReplayClock(endsMs, reduceMotion);

  return (
    <div ref={playerRef}>
      <div className={!playing && !ended && atMs > 0 ? "[&_*]:[animation-play-state:paused]" : undefined}>
        <ReplayFrame replay={replay} timeline={timeline} events={events} tone={tone} atMs={atMs} reduceMotion={reduceMotion} />
      </div>
      <ReplayControls
        state={ended ? "ended" : playing ? "playing" : "paused"}
        progress={atMs / endsMs}
        onToggle={toggle}
      />
    </div>
  );
}
